// What a locked text box's style looks like on the prep screen, and how it
// is measured, so the page shows what the stamper will print.
//
// Nate, 2026-10-26: change color, indentation, size, boldness, italics or
// underline on fixed text. The rules (palette, bounds, validation) live in
// shared/esign/text-style.ts; this file only turns a style into CSS and into
// the numbers the shared line layout needs.

import type { CSSProperties } from "react";

import {
  layoutMultilineText,
  type MeasureText,
  type MultilineLayout,
} from "@shared/esign/multiline-text";
import { DEFAULT_PRINTED_FONT_PT } from "@shared/esign/field-types";
import type { EsignTextStyle } from "@shared/esign/text-style";

import { MULTILINE_SCREEN_FONT_FAMILY } from "./multiline-measure";

/** The CSS for a style. Empty for no style, so an unstyled box is untouched. */
export function textStyleCss(style: EsignTextStyle | undefined | null): CSSProperties {
  if (!style) return {};
  return {
    ...(style.bold ? { fontWeight: 700 } : {}),
    ...(style.italic ? { fontStyle: "italic" } : {}),
    ...(style.underline ? { textDecoration: "underline" } : {}),
    ...(style.color ? { color: style.color } : {}),
  };
}

let context: CanvasRenderingContext2D | null | undefined;

function measuringContext(): CanvasRenderingContext2D | null {
  if (context !== undefined) return context;
  try {
    context = document.createElement("canvas").getContext("2d");
  } catch {
    context = null;
  }
  return context;
}

/** A measurer set in the same face (bold and/or italic) the stamper embeds,
 *  so the wrap the sender sees is the wrap that prints. */
export function measureStyledText(style: EsignTextStyle | undefined | null): MeasureText {
  const prefix = `${style?.italic ? "italic " : ""}${style?.bold ? "bold " : ""}`;
  return (text, fontSizePt) => {
    const ctx = measuringContext();
    if (!ctx) return text.length * fontSizePt * (style?.bold ? 0.55 : 0.5);
    ctx.font = `${prefix}${fontSizePt}px ${MULTILINE_SCREEN_FONT_FAMILY}`;
    return ctx.measureText(text).width;
  };
}

/** A multi-line box's lines with its style applied: the box narrows by the
 *  indent and the type size is the style's, exactly as document-stamping.ts
 *  does it, so screen and print cannot disagree about where line k wraps. */
export function layoutStyledMultiline(
  field: {
    x?: number;
    y?: number;
    width: number;
    height: number;
    linePitch: number;
    fontSize?: number | undefined;
    firstLineIndent?: number | undefined;
    textStyle?: EsignTextStyle | undefined;
  },
  text: string,
): MultilineLayout {
  const indent = field.textStyle?.indent ?? 0;
  return layoutMultilineText(
    {
      x: (field.x ?? 0) + indent,
      y: field.y ?? 0,
      width: Math.max(1, field.width - indent),
      height: field.height,
      linePitch: field.linePitch,
    },
    text,
    field.textStyle?.size ?? field.fontSize ?? DEFAULT_PRINTED_FONT_PT,
    measureStyledText(field.textStyle),
    field.firstLineIndent ?? 0,
  );
}
